/**
 * Human-facing naming for a Property.
 *
 * `Property.domain` is nullable — PERSON and CATEGORY subjects have no website —
 * so interpolating it straight into a title or an email subject renders "null"
 * or a blank. Every user-visible reference to a property should go through
 * {@link getPropertyLabel} instead of reading `.domain` directly.
 */

/** The subset of a Property (or a report's embedded property) needed to name it. */
export interface PropertyLabelSource {
  companyName?: string | null;
  domain?: string | null;
}

/**
 * The best available human-readable name for a property: its brand/person name,
 * falling back to its domain, falling back to a generic placeholder.
 *
 * @param property - Any object carrying `companyName` and/or `domain`
 * @param fallback - Used when the property has neither; defaults to "this subject"
 *
 * @example
 * getPropertyLabel({ companyName: "Acme", domain: "acme.com" }) // "Acme"
 * getPropertyLabel({ companyName: null, domain: "acme.com" })   // "acme.com"
 * getPropertyLabel({ companyName: "Jane Roe", domain: null })   // "Jane Roe"
 * getPropertyLabel({ companyName: null, domain: null })         // "this subject"
 */
export function getPropertyLabel(
  property: PropertyLabelSource | null | undefined,
  fallback = "this subject",
): string {
  const companyName = property?.companyName?.trim();
  if (companyName) {
    return companyName;
  }

  const domain = property?.domain?.trim();
  if (domain) {
    return domain;
  }

  return fallback;
}

/**
 * Filesystem-safe form of {@link getPropertyLabel}, for export filenames.
 *
 * @example
 * getPropertySlug({ companyName: "Sanford Heisler Sharp", domain: null }) // "sanford-heisler-sharp"
 */
export function getPropertySlug(
  property: PropertyLabelSource | null | undefined,
  fallback = "report",
): string {
  const slug = getPropertyLabel(property, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug || fallback;
}
